import { AfterViewInit, Directive, ElementRef, NgZone, OnDestroy } from '@angular/core';

/**
 * Balances a wrapping pill strip (an ngx-bootstrap tabset styled by .tabpills) so the tabs fill
 * the width in rows of equal count, instead of eight on one row and an orphan on the next.
 *
 *   <tabset class="tabpills" appBalancedTabs>
 *
 * The record's tab set varies by project (Support Assessment, Accommodation and others switch on
 * with modules) and the window varies by user, so no fixed layout holds (Ronan 2026-10-15). The
 * directive measures the visible tabs at their natural width, takes the most that fit on a row at
 * the widest label's width (every tab gets the same share, so a share narrower than the widest label
 * would wrap that label inside its pill - never, per the design), splits the tabs evenly over the
 * rows that gives and hands the per-row count to the stylesheet as --tab-cols (.tabpills.balanced gives each tab a flex basis of one share of the
 * row, so every row fills the width; the last row shares what it has). It re-runs when the strip's
 * width changes (ResizeObserver) and when a tab appears or hides (the module tabs flip their class
 * once the user info lands; MutationObserver on the items). Measuring toggles .measuring, which lets
 * the tabs fall back to their natural width for one synchronous layout read, so nothing in between
 * is painted. DOM-only, outside the Angular zone.
 */
@Directive({
  selector: 'tabset[appBalancedTabs]',
  standalone: true
})
export class BalancedTabsDirective implements AfterViewInit, OnDestroy {
  private resize?: ResizeObserver;
  private mutations?: MutationObserver;
  private frame = 0;
  private cols = 0;

  constructor(private host: ElementRef<HTMLElement>, private zone: NgZone) {}

  ngAfterViewInit(): void {
    const nav = this.nav();
    if (!nav) return;
    this.balance();
    this.zone.runOutsideAngular(() => {
      this.resize = new ResizeObserver(() => this.schedule());
      this.resize.observe(nav);
      this.mutations = new MutationObserver((records) => {
        // Only the items' changes matter; the strip's own attributes are not observed here but
        // the guard keeps a later subtree widening from looping on our own writes.
        if (records.some((r) => r.target !== nav)) this.schedule();
      });
      this.mutations.observe(nav, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
    });
  }

  ngOnDestroy(): void {
    this.resize?.disconnect();
    this.mutations?.disconnect();
    cancelAnimationFrame(this.frame);
  }

  private nav(): HTMLElement | null {
    return this.host.nativeElement.querySelector(':scope > ul.nav');
  }

  private schedule(): void {
    cancelAnimationFrame(this.frame);
    this.frame = requestAnimationFrame(() => this.balance());
  }

  /** Natural widths of the visible tabs, the strip's inner width and gap, then the even split. */
  private balance(): void {
    const nav = this.nav();
    if (!nav) return;
    const host = this.host.nativeElement;
    const items = Array.from(nav.children).filter(
      (el): el is HTMLElement => el instanceof HTMLElement && el.offsetParent !== null
    );
    const style = getComputedStyle(nav);
    const inner = nav.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const gap = parseFloat(style.columnGap) || 0;
    if (items.length < 2 || !(inner > 0)) {
      this.apply(host, nav, 0);
      return;
    }

    host.classList.add('measuring');
    const widths = items.map((li) => li.getBoundingClientRect().width);
    host.classList.remove('measuring');

    // The most tabs a row holds when each gets the widest label's width (shares are equal, so that
    // is what keeps every label on one line), then the tabs spread evenly over the rows that gives:
    // the even split only lowers the count, so every share stays at least the widest label.
    const n = items.length;
    const widest = Math.max(...widths);
    let cols = Math.max(1, Math.min(n, Math.floor((inner + gap) / (widest + gap))));
    const rows = Math.ceil(n / cols);
    cols = Math.ceil(n / rows);
    this.apply(host, nav, cols);
  }

  private apply(host: HTMLElement, nav: HTMLElement, cols: number): void {
    if (cols === this.cols) return;
    this.cols = cols;
    if (cols > 0) {
      nav.style.setProperty('--tab-cols', String(cols));
      host.classList.add('balanced');
    } else {
      nav.style.removeProperty('--tab-cols');
      host.classList.remove('balanced');
    }
  }
}
